import { Space, Typography, Upload, message } from "antd";
import type { RcFile, UploadFile, UploadProps } from "antd/es/upload/interface";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import VideoIcon from "../../assets/icons/jouer.svg";
import LockIcon from "../../assets/icons/lock-outlined.svg";
import PlusIcon from "../../assets/icons/plus-black.svg";
import AddCircleGrey from "../../assets/icons/add-circle-grey.svg";
import AudioIcon from "../../assets/icons/white-audio.svg";
import AudioThumbnail from "../common/AudioThumbnail/AudioThumbnail";
import { fileService } from "../../services";
import { compressImage, convertHeicFile, generateThumbnailURL, isImageAcceptedFormat } from "../../utls/FunctionsUtil";
import MailboxMediaModal from "../MailboxMediaModal";
import RecordAudioModal from "../RecordAudioModal/RecordAudioModal";
import SelectQuickFileAlbumModal from "../chatScripts/modals/SelectQuickFileAlbumModal/SelectQuickFileAlbumModal";
import SelectQuickFileMediaModal, { QuickFileMedia } from "../chatScripts/modals/SelectQuickFileMediaModal/SelectQuickFileMediaModal";
import "./index.less";

type props = {
  disabled?: boolean;
  maxCount?: number;
  defaultList?: any[];
  onFilesChange?: (fileList: UploadFile<[]>[]) => void;
  conversationId?: string;
  receiverId?: string;
  messageId?: string;
  quickFiles?: any;
  allFiles?: any;
  fromCollection?: boolean;
  collectionTitle?: string;
};

const MediaPushMediaComponent: React.FC<props> = ({
  disabled = false,
  maxCount,
  defaultList,
  onFilesChange,
  conversationId,
  messageId,
  receiverId,
  quickFiles,
  allFiles,
  fromCollection = false,
  collectionTitle = "",
}) => {
  const { t } = useTranslation();
  const [fileList, setFileList] = useState<UploadFile[]>(allFiles);
  const [isMediaModal, setIsMediaModal] = useState(false);
  const [showAudioModal, setShowAudioModal] = useState(false);
  const [showQuickAlbumsModal, setShowQuickAlbumsModal] = useState(false);
  const [selectedQuickAlbumId, setSelectedQuickAlbumId] = useState<string | null>(null);
  const [selectedAlbumType, setSelectedAlbumType] = useState<"media" | "audio">("media");
  const uploadRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const params: any = new URLSearchParams(location.search);
  const paramsFiles: Array<any[]> = JSON.parse(params.get("selected"));
  const [paramIds, setParamIds] = useState<any>([]);

  const getAllSelectedFiles = async (fileIds: Array<any>) => {
    const selectedFileIds = fileList.map((file) => file.uid);
    const filesToFetch = fileIds.filter((id) => !selectedFileIds.includes(id));
    await fileService.allSelectedFiles(filesToFetch).then((response: any) => {
      const filesToAdd: any = [];
      response.forEach((item: any) => {
        let fileType = "";
        let fileExt = "";
        let isVideo = false;
        let isAudio = false;
        if (item.url.includes(".mp4")) {
          fileType = "video/mp4";
          fileExt = "mp4";
          isVideo = true;
        } else if (item.url.includes(".mp3")) {
          fileType = "audio/mp3";
          fileExt = "mp3";
          isAudio = true;
        } else if (item.url.includes(".wav")) {
          fileType = "audio/wav";
          fileExt = "wav";
          isAudio = true;
        } else if (item.url.includes(".ogg")) {
          fileType = "audio/ogg";
          fileExt = "ogg";
          isAudio = true;
        } else if (item.url.includes(".m4a")) {
          fileType = "audio/m4a";
          fileExt = "m4a";
          isAudio = true;
        } else {
          fileType = "image/webp";
          fileExt = "webp";
        }
        let originFileObj2 = {
          isPrivate: true,
          status: "done",
          uid: item._id,
          name: item._id,
          percent: 0,
          thumbUrl: "",
          type: "quick-link",
        };
        let JsonString = JSON.stringify(originFileObj2);
        let blob = new Blob([JsonString], { type: fileType });
        let file = new File([blob], `newQuickFile.${fileExt}`, { type: fileType });

        filesToAdd.push({
          ...item,
          imgType: "quick-file",
          isPrivate: true,
          status: "done",
          uid: item._id,
          name: item._id,
          percent: 0,
          thumbUrl: isVideo ? item?.thumbnailUrl : isAudio ? item?.thumbnailUrl || item?.url : item?.url,
          type: fileType,
          originFileObj: file,
        });

        //setQuickFiles((prev: any) => [...prev, { id: item._id, isPrivate: true }]);
      });

      setFileList((prevFiles: any) => {
        return [...prevFiles, ...filesToAdd];
      });
    });
  };

  useEffect(() => {
    if (paramsFiles?.length !== 0) {
      setParamIds(paramsFiles);
    }
    return () => {
      setParamIds([]);
    };
  }, []);

  useEffect(() => {
    if (paramIds !== null && paramIds !== undefined && paramIds?.length !== 0) {
      getAllSelectedFiles(paramIds);
    }
  }, [paramIds]);

  useEffect(() => {
    if (onFilesChange !== undefined) {
      onFilesChange(fileList);
    }
  }, [fileList]);

  const customItemRender = (originNode: React.ReactNode, file: any) => {
    let lock: boolean = false;
    let isThumbnail: boolean = false;
    let isVideo: boolean = file.type?.includes("video")!;
    let isAudio: boolean = file.type?.includes("audio")!;

    defaultList?.forEach((e) => {
      if (file.uid === e.uid) {
        if (e.isPrivate) {
          lock = true;
        }
        isThumbnail = e.isThumbnail;
      }
    });

    const content = isAudio ? (
      <div className="clickable-image-wrapper" onClick={() => onRemove(file)}>
        <AudioThumbnail />
        {lock && <img src={LockIcon} width={20} style={{ position: "relative", top: -150, right: -70, color: "white" }} alt="lock icon" />}
        {/* <div className="remove-overlay">
          <span className="remove-icon">×</span>
        </div> */}
      </div>
    ) : (
      <>
        {originNode}
        {fromCollection && isThumbnail && (
          <div className="collection-thumbnail">
            <Typography.Text className="collection-thumbnail-text">{t("miniature")}</Typography.Text>
          </div>
        )}
        {lock && (
          <img
            src={LockIcon}
            width={30}
            style={{ position: "relative", top: fromCollection && isThumbnail ? -110 : -90, right: -35, color: "white" }}
            alt="lock icon"
          />
        )}
        {isVideo && (
          <img src={VideoIcon} style={!lock ? { top: -147, right: -73 } : { top: -160, right: -45 }} alt="video icon" className="videoIcon" />
        )}
      </>
    );

    return content;
  };

  const beforeUpload: UploadProps["beforeUpload"] = async (file: RcFile): Promise<File | boolean | string> => {
    try {
      //console.log("in before **upload** file", file.uid, file);

      // Block audio files in collections
      if (fromCollection && file.type.includes("audio/")) {
        message.error(t("unsupported-file-format-error-message"));
        return Upload.LIST_IGNORE;
      }

      // If the file is in an accepted format, proceed without conversion
      if (isImageAcceptedFormat(file.type)) {
        // compress file
        const compressedImage = await compressImage(file);
        //console.log("beforeUpload 1 compressedImage", compressedImage);
        if (compressedImage !== null) {
          handleConvertedFile(file.uid, compressedImage, true);
        }
        //handleConvertedFile(file.uid, file, true);
        return false;
      } else if (file.type === "image/heic" || file.type === "") {
        handleConvertedFile(file.uid, file, false);
        // convert file
        const convertedFile = await convertHeicFile(file);
        //console.log("beforeUpload 2 convertedFile", convertedFile);
        // compress file
        const compressedImage = await compressImage(convertedFile);
        //console.log("beforeUpload 3 compressedImage", compressedImage);
        if (compressedImage !== null) {
          handleConvertedFile(file.uid, compressedImage, true);
        }
        return false;
      } else if (file.type.includes("video/")) {
        handleConvertedFile(file.uid, file, true);
        return false;
      } else if (file.type.includes("audio/")) {
        handleConvertedFile(file.uid, file, true);
        return false;
      } else {
        message.error(t("unsupported-file-format-error-message"));
        return Upload.LIST_IGNORE;
      }
    } catch (error) {
      removeUnsupportedFiles(file.uid);
      message.error(t("unsupported-file-format-error-message"));
      return Upload.LIST_IGNORE;
    }
  };

  const handleConvertedFile = async (uid: string, file: File, converted: boolean) => {
    if (!converted) {
      const fileObj: UploadFile = {
        uid: uid,
        name: file.name,
        status: "uploading",
        percent: 50,
      };
      setFileList((prevList) => [fileObj, ...prevList]);
    } else {
      const fileObj: UploadFile = {
        uid: uid,
        name: file.name,
        status: "done",
        type: file.type,
        size: file.size,
        originFileObj: file as RcFile,
      };
      if (file.type.includes("video")) {
        const result = await generateThumbnailURL(file as RcFile);
        fileObj.url = result;
      } else if (file.type.includes("audio")) {
        // For audio files, use the imported AudioIcon as the thumbnail
        fileObj.url = AudioIcon;
      }
      setFileList((prevList) => [fileObj, ...prevList.filter((file) => file.uid !== fileObj.uid)]);
    }
  };

  const isImageUrl = (file: UploadFile): boolean => {
    // Do not render as image for audio, we render a custom AudioThumbnail
    if (file.type?.includes("audio/")) return false;
    return true;
  };

  const removeUnsupportedFiles = (uid: string) => {
    setFileList((prevList) => [...prevList.filter((file) => file.uid !== uid)]);
  };

  const onRemove = async (file: UploadFile<any>) => {
    //setAllFiles((prev: any) => prev.filter((f: any) => f.uid !== file.uid));
    //setQuickFiles((prev: any) => prev.filter((f: any) => f.id !== file.uid));
    setFileList((prev: any) =>
      prev.filter((f: any) => {
        return f.uid !== file.uid;
      })
    );
  };

  const handleDeviceClick = () => {
    // Close modal before triggering input to avoid UI glitches
    setIsMediaModal(false);
    setTimeout(() => {
      uploadRef.current?.click();
    }, 100);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    Array.from(files).forEach((file) => {
      const uid = `upload_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
      handleFileUpload(uid, file as RcFile);
    });

    e.target.value = "";
  };

  const handleFileUpload = async (uid: string, file: RcFile) => {
    try {
      // Block audio files in collections
      if (fromCollection && file.type.includes("audio/")) {
        message.error(t("unsupported-file-format-error-message"));
        return;
      }

      if (isImageAcceptedFormat(file.type)) {
        const compressedImage = await compressImage(file);
        if (compressedImage !== null) {
          handleConvertedFile(uid, compressedImage, true);
        }
      } else if (file.type === "image/heic" || file.type === "") {
        handleConvertedFile(uid, file, false);
        const convertedFile = await convertHeicFile(file);
        const compressedImage = await compressImage(convertedFile);
        if (compressedImage !== null) {
          handleConvertedFile(uid, compressedImage, true);
        }
      } else if (file.type.includes("video/")) {
        handleConvertedFile(uid, file, true);
      } else if (file.type.includes("audio/")) {
        handleConvertedFile(uid, file, true);
      } else {
        message.error(t("unsupported-file-format-error-message"));
      }
    } catch (error) {
      setFileList((prevList) => prevList.filter((f) => f.uid !== uid));
      message.error(t("unsupported-file-format-error-message"));
    }
  };

  const handleOpenAudioRecorder = () => {
    setIsMediaModal(false);
    setTimeout(() => {
      setShowAudioModal(true);
    }, 100);
  };

  const handleAudioReady = async (audioBlob: Blob, audioTitle: string) => {
    const audioFile = new File([audioBlob], `${audioTitle || "audio_recording"}.mp3`, { type: "audio/mpeg" });
    const uid = `upload_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    handleFileUpload(uid, audioFile as RcFile);
    setShowAudioModal(false);
  };

  const handleQuickFilesClick = () => {
    setIsMediaModal(false);
    setShowQuickAlbumsModal(true);
  };

  const handleQuickFilesConfirm = (selectedFiles: QuickFileMedia[]) => {
    const filesToAdd: any[] = [];
    selectedFiles.forEach((item: QuickFileMedia) => {
      // Block audio files in collections
      if (fromCollection && item.isAudio) {
        message.error(t("unsupported-file-format-error-message"));
        return;
      }

      let fileType = "";
      let fileExt = "";
      if (item.isVideo) {
        fileType = "video/mp4";
        fileExt = "mp4";
      } else if (item.isAudio) {
        if (item.url?.includes(".wav")) {
          fileType = "audio/wav";
          fileExt = "wav";
        } else if (item.url?.includes(".m4a")) {
          fileType = "audio/m4a";
          fileExt = "m4a";
        } else if (item.url?.includes(".ogg")) {
          fileType = "audio/ogg";
          fileExt = "ogg";
        } else {
          fileType = "audio/mp3";
          fileExt = "mp3";
        }
      } else {
        fileType = "image/webp";
        fileExt = "webp";
      }

      const originFileObj = {
        isPrivate: true,
        status: "done",
        uid: item.id,
        name: item.id,
        percent: 0,
        thumbUrl: "",
        type: "quick-link",
      };
      const JsonString = JSON.stringify(originFileObj);
      const blob = new Blob([JsonString], { type: fileType });
      const file = new File([blob], `newQuickFile.${fileExt}`, { type: fileType });

      filesToAdd.push({
        uid: item.id,
        name: item.id,
        imgType: "quick-file",
        isPrivate: true,
        status: "done",
        percent: 0,
        thumbUrl: item.thumbnail || item.url,
        type: fileType,
        url: item.url,
        originFileObj: file,
      });
    });

    setFileList((prevFiles: any) => {
      const existingIds = new Set(prevFiles.map((f: any) => f.uid));
      const newFiles = filesToAdd.filter((f: any) => !existingIds.has(f.uid));
      return [...prevFiles, ...newFiles];
    });

    setSelectedQuickAlbumId(null);
  };

  return (
    <div className={`w-full ${fileList.length === 0 ? "mediaPushMediaWrapper" : "addMediaWrapperRectangularShape"} `}>
      {fileList.length === 0 ? (
        <div className="d-flex justify-content-center" onClick={() => setIsMediaModal(true)}>
          {(!maxCount || fileList.length < (maxCount || 0)) && <img src={AddCircleGrey} width={150} height={150} alt="add" />}
        </div>
      ) : (
        <div className="rectangularShape" onClick={() => setIsMediaModal(true)}>
          {maxCount ? (
            fileList.length < maxCount && (
              <div className={`addCircle ${disabled ? "disabled" : ""}`}>
                <img src={PlusIcon} width={70} alt="plus" />
              </div>
            )
          ) : (
            <div className={`addCircle ${disabled ? "disabled" : ""}`}>
              <img src={PlusIcon} width={70} alt="plus" />
            </div>
          )}
        </div>
      )}

      <MailboxMediaModal
        isOpen={isMediaModal}
        onClose={() => setIsMediaModal(false)}
        onMyAlbums={handleQuickFilesClick}
        onMyDevice={handleDeviceClick}
        // onRecordAudio={handleOpenAudioRecorder}
      />

      {showQuickAlbumsModal && (
        <SelectQuickFileAlbumModal
          onClose={() => setShowQuickAlbumsModal(false)}
          onAlbumSelect={(albumId, type) => {
            setSelectedQuickAlbumId(albumId);
            setSelectedAlbumType(type);
            setShowQuickAlbumsModal(false);
          }}
          hideAudioTab={fromCollection}
        />
      )}

      {selectedQuickAlbumId && (
        <SelectQuickFileMediaModal
          albumId={selectedQuickAlbumId}
          type={selectedAlbumType}
          onClose={() => {
            setSelectedQuickAlbumId(null);
            setSelectedAlbumType("media");
          }}
          onConfirm={handleQuickFilesConfirm}
          onBack={() => {
            setSelectedQuickAlbumId(null);
            setSelectedAlbumType("media");
            setShowQuickAlbumsModal(true);
          }}
        />
      )}

      <RecordAudioModal isOpen={showAudioModal} onClose={() => setShowAudioModal(false)} onAudioReady={handleAudioReady} isFromConv={false} />

      <div>
        <Upload
          accept={fromCollection ? "image/*,video/*" : "image/*,video/*,audio/*,.mp3,.wav,.m4a,.aac,.ogg,.webm"}
          disabled={disabled}
          action=""
          itemRender={(originNode: any, file: any) => customItemRender(originNode, file)}
          defaultFileList={defaultList}
          fileList={fileList}
          listType="picture-card"
          maxCount={maxCount}
          onRemove={onRemove}
          //onChange={onChangeU}
          beforeUpload={beforeUpload}
          isImageUrl={isImageUrl}
          showUploadList={{
            showRemoveIcon: true,
            showPreviewIcon: false,
          }}
          multiple={true}
        >
          <div></div>
        </Upload>
        <input
          type="file"
          ref={uploadRef}
          style={{ display: "none" }}
          multiple
          accept="image/*,video/*,audio/*,.mp3,.wav,.m4a,.aac,.ogg,.webm"
          onChange={handleFileInputChange}
        />
      </div>
    </div>
  );
};

export default MediaPushMediaComponent;
